// src/auth/Protected.jsx
import React from "react";
import { Navigate, useLocation } from "react-router-dom";
import {
  buildControlledAccessHoldingHref,
  hasAppInteriorGrant,
  resolveAuthenticatedHoldHref,
  resolveHoldReason,
  shouldHoldAuthenticatedUser,
  useAuthSession,
} from "@/features/auth";
import { deriveSessionRole } from "@/features/auth/deriveSessionRole";
import { SessionLoadingShell } from "@/features/auth/SessionLoadingShell";

/**
 * Wrap protected pages — fail-closed session + interior grant (B1/B5).
 * Deep links preserved via redirectTo on /access holding.
 */
export function ProtectedRoute({ children }) {
  const { status, uid, access, error } = useAuthSession();
  const location = useLocation();

  const nextPath = `${location.pathname}${location.search}${location.hash}`;

  if (status === "loading") {
    return <SessionLoadingShell label="Verifying access…" />;
  }

  if (status !== "authenticated" || !uid) {
    const reason = resolveHoldReason("anonymous", access, error);
    return (
      <Navigate
        to={buildControlledAccessHoldingHref({ redirectTo: nextPath, reason })}
        replace
      />
    );
  }

  if (shouldHoldAuthenticatedUser(access) || !hasAppInteriorGrant(access)) {
    return (
      <Navigate
        to={resolveAuthenticatedHoldHref({
          redirectTo: nextPath,
          access,
          sessionError: error,
        })}
        replace
      />
    );
  }

  return children;
}

/**
 * Role gate for privileged areas.
 * - allow: array of roles that can enter (e.g., ["admin", "enterprise"])
 * - deny:  array of roles explicitly blocked
 * - fallback: what to render if blocked (null by default)
 */
export function RoleGate({ allow, deny, fallback = null, children }) {
  const session = useAuthSession();
  const role = deriveSessionRole(session.status, session.access, session.legacy);

  if (session.status === "loading") {
    return <SessionLoadingShell />;
  }

  if (Array.isArray(deny) && deny.includes(role)) return fallback;

  if (Array.isArray(allow) && allow.length > 0) {
    return allow.includes(role) ? children : fallback;
  }

  return children;
}
